import { motion } from "framer-motion";
import SEO from "../components/SEO";

export default function Privacy() {
  return (
    <>
      <SEO 
        title="Privacy Policy | AI Film Society"
        description="Privacy Policy for AI Film Society - Learn how we collect, use, and protect your personal information."
      />
      
      <div className="min-h-screen py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-syne font-bold text-4xl sm:text-5xl text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-slate-400 mb-12">
              Last updated: January 24, 2026
            </p>

            <div className="prose prose-invert prose-purple max-w-none space-y-8">
              <section>
                <h2 className="font-syne text-2xl text-white mb-4">1. Introduction</h2>
                <p className="text-slate-300 leading-relaxed">
                  AI Film Society ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">2. Information We Collect</h2>
                
                <h3 className="font-syne text-xl text-white mt-6 mb-3">Personal Information</h3>
                <p className="text-slate-300 leading-relaxed mb-4">
                  When you create an account or use our services, we may collect:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Name and email address</li>
                  <li>Profile information (bio, location, skills, social links)</li>
                  <li>Profile picture</li>
                  <li>Content you submit (films, forum posts, job listings)</li>
                  <li>Payment information (processed securely by Stripe)</li>
                </ul>

                <h3 className="font-syne text-xl text-white mt-6 mb-3">Automatically Collected Information</h3>
                <p className="text-slate-300 leading-relaxed mb-4">
                  When you visit our website, we automatically collect:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>IP address and browser type</li>
                  <li>Device information</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring website</li>
                </ul>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">3. How We Use Your Information</h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Provide and maintain our services</li>
                  <li>Process your account registration</li>
                  <li>Display your profile in our member directory</li>
                  <li>Process film submissions and job postings</li>
                  <li>Process donations and payments</li>
                  <li>Send you updates and notifications (with your consent)</li>
                  <li>Improve our website and services</li>
                  <li>Respond to your inquiries and support requests</li>
                  <li>Detect and prevent fraud or abuse</li>
                </ul>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">4. Information Sharing</h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  We may share your information in the following circumstances:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li><strong>Public Profile:</strong> Your name, bio, and other profile information you choose to make public will be visible to other members</li>
                  <li><strong>Service Providers:</strong> We share data with trusted third parties who help us operate our services (e.g., Stripe for payments)</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger or acquisition, your information may be transferred</li>
                </ul>
                <p className="text-slate-300 leading-relaxed mt-4">
                  We do not sell your personal information to third parties.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">5. Third-Party Services</h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Our website integrates with third-party services:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li><strong>Google OAuth:</strong> For account authentication</li>
                  <li><strong>Stripe:</strong> For secure payment processing</li>
                  <li><strong>Leaflet/OpenStreetMap:</strong> For event maps</li>
                </ul>
                <p className="text-slate-300 leading-relaxed mt-4">
                  These services have their own privacy policies, and we encourage you to review them.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">6. Data Security</h2>
                <p className="text-slate-300 leading-relaxed">
                  We implement appropriate security measures to protect your personal information, including encryption, secure servers, and access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">7. Data Retention</h2>
                <p className="text-slate-300 leading-relaxed">
                  We retain your personal information for as long as your account is active or as needed to provide you services. You may request deletion of your account and associated data by contacting us.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">8. Your Rights</h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Depending on your location, you may have the following rights:
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                  <li><strong>Deletion:</strong> Request deletion of your data</li>
                  <li><strong>Portability:</strong> Request transfer of your data</li>
                  <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                </ul>
                <p className="text-slate-300 leading-relaxed mt-4">
                  To exercise these rights, please contact us at the email below.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">9. Cookies</h2>
                <p className="text-slate-300 leading-relaxed">
                  We use cookies and similar technologies to maintain your session and improve your experience. You can control cookies through your browser settings, but disabling them may affect functionality.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">10. Children's Privacy</h2>
                <p className="text-slate-300 leading-relaxed">
                  Our services are not intended for children under 13. We do not knowingly collect information from children. If you believe we have collected information from a child, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">11. International Users</h2>
                <p className="text-slate-300 leading-relaxed">
                  If you are accessing our services from outside the United States, please be aware that your information may be transferred to and processed in the United States, where data protection laws may differ.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">12. Changes to This Policy</h2>
                <p className="text-slate-300 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="font-syne text-2xl text-white mb-4">13. Contact Us</h2>
                <p className="text-slate-300 leading-relaxed">
                  If you have questions about this Privacy Policy or our data practices, please contact us at{" "}
                  <a href="mailto:sam@aiadstudio.com" className="text-purple-light hover:text-purple-lighter">
                    sam@aiadstudio.com
                  </a>
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
